// react
import { useMemo } from 'react';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';

// api
import { ReadAlarmDto } from '@/api/generated/recalc/endpoints/v1/alarms';
import { useGetV2DataQuery } from '@/api/generated/recalc/endpoints/v2/data';

// relative
import AlarmTypeCardHeader from '../../AlarmTypeCardHeader';

import { DateTime } from 'luxon';

type AlarmDataStreamAssociation = {
  unitId?: number;
  unitTypeInputId?: number | null;
  dataType?: string | null;
};

type DataQualityThresholdAlarm = ReadAlarmDto & {
  Associations?: ReadAlarmDto['Associations'] & {
    DataStreams?: AlarmDataStreamAssociation[];
    Units?: number[];
  };
};

const DataQualityThreshold = ({
  alarm,
}: {
  alarm: DataQualityThresholdAlarm;
}) => {
  const units = alarm?.Associations?.Units || [];
  const dataStreams = alarm?.Associations?.DataStreams || [];
  const primaryStream = dataStreams[0];

  const dataFrom = alarm?.DataFrom
    ? DateTime.fromISO(alarm.DataFrom, { zone: 'utc' })
    : null;
  const dataTo = alarm?.DataTo
    ? DateTime.fromISO(alarm.DataTo, { zone: 'utc' })
    : null;

  const paddedStart =
    dataFrom && dataFrom.isValid ? dataFrom.minus({ hours: 0 }) : null;

  const now = DateTime.utc();
  const paddedEndCandidate =
    dataTo && dataTo.isValid ? dataTo.plus({ hours: 0 }) : now;
  const paddedEnd =
    paddedEndCandidate.toMillis() > now.toMillis() ? now : paddedEndCandidate;

  const queryStartUtc = paddedStart
    ?.toUTC()
    .toISO({ suppressMilliseconds: true });
  const queryEndUtc = paddedEnd.toUTC().toISO({ suppressMilliseconds: true });

  const canQuery =
    Boolean(primaryStream?.dataType) &&
    typeof primaryStream?.unitId === 'number' &&
    // typeof primaryStream?.unitTypeInputId === 'number' &&
    Boolean(paddedStart?.isValid);

  const { data: streamData = [] } = useGetV2DataQuery(
    {
      unitId: primaryStream?.unitId ?? 0,
      unitTypeInputId: primaryStream?.unitTypeInputId ?? 0,
      dataType: primaryStream?.dataType || '',
      aggregateType: 'Hour',
      start: queryStartUtc || '',
      end: queryEndUtc || undefined,
      orderBy: 'Timestamp',
      orderDirection: 'asc',
    },
    {
      skip: !canQuery,
    },
  );

  const chartSeries = useMemo(() => {
    if (!streamData.length || !primaryStream) return [];

    const points: Array<[number, number | null]> = streamData
      .filter((point) => point.timestamp)
      .map((point) => [
        DateTime.fromISO(String(point.timestamp), { zone: 'utc' }).toMillis(),
        point.value ?? null,
      ]);

    return [
      {
        id: `${primaryStream.unitId}-${primaryStream.dataType}-${primaryStream.unitTypeInputId}`,
        name: `${primaryStream.dataType} (${primaryStream.unitId})`,
        type: 'line' as const,
        data: points,
        dataType: primaryStream.dataType,
      },
    ];
  }, [primaryStream, streamData]);

  console.log('Chart series data:', chartSeries);

  const unitId = units[0] ?? null;

  console.log('Alarm in DataQualityThreshold:', alarm);

  const viewUnitText = 'View Unit';
  return (
    <Card>
      <AlarmTypeCardHeader alarm={alarm} title="Data Quality Threshold" />
      <Divider />
      <CardContent>
        <Typography variant="body1">{alarm.Details}</Typography>
      </CardContent>
      {/* <CardContent>
        <ThresholdExceededChart
          series={chartSeries}
          alarm={alarm}
          isLoading={isLoading}
          threshold={threshold}
        />
      </CardContent> */}
      <Divider />
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        {unitId ? (
          <Button
            component={Link}
            to={'/$accountId/units/' + unitId}
            size="small"
            color="primary"
          >
            {viewUnitText}
          </Button>
        ) : null}
      </CardActions>
    </Card>
  );
};

export default DataQualityThreshold;
